import grapesjs, { Editor, EditorConfig, EditorModelParam, usePlugin } from 'grapesjs';
import GjsEditor, { AssetsProvider, Canvas, useEditor } from '@grapesjs/react';
import { MAIN_BORDER_COLOR } from './Components/common';
import RightSidebar from './Components/RightSideBar.web';
import CustomAssetManager from './Components/CustomAssetManager';
import Topbar from './Components/TopBar.web';
import React, { createRef, useCallback, useEffect, useRef, useState } from 'react';
import { View, Image, Alert, useWindowDimensions } from 'react-native';
import apis from '@api/api';
import { Modal } from '@constants/Themed';
import Button from '@components/UI/Button/Button';
import WebView from 'react-native-webview';
import html2canvas from 'html2canvas';
import { useEditorMaybe, } from '@grapesjs/react';
import duplicate from './utils/duplicateEmailTemplate';
import { SHOW_AUTH_MODAL } from '@reduxLocal/actions/types';
import { useDispatch } from 'react-redux';
import { HOST } from '@api/constant';
import { LinearGradient } from 'expo-linear-gradient';
import 'grapesjs/dist/css/grapes.min.css';
import './CustomEditor.css';

const reader = new FileReader();


export const gjsOptions: EditorConfig = {
  height: '80vh',
    canvasCss: `
      body {
        margin: 0 !important;
        padding: 0 !important;
        width: 100% !important;
        -webkit-text-size-adjust: 100%;
        -ms-text-size-adjust: 100%;
        background: #fff;
      }
      table, td {
        mso-table-lspace: 0pt;
        mso-table-rspace: 0pt;
      }
      img {
        border: 0;
        height: auto;
        line-height: 100%;
        outline: none;
        text-decoration: none;
        display: block;
        max-width: 100%;
      }
      a {
        text-decoration: none;
        color: inherit;
      }
      * {
        box-sizing: border-box;
      }
    `,
    storageManager: false,
    undoManager: { trackSelection: false },
    selectorManager: {
      componentFirst: true,
      escapeName: (name) => name.toLowerCase(), // Ensures all tags are lowercase
    },
    assetManager: {
      showUrlInput: true,
      upload: __DEV__ ? `http://${HOST}/api/templates/upload-media` : 'https://api.smartsaas.pro/api/templates/upload-media',
      uploadName: 'files',
      credentials: 'include',
      stylePrefix: 'am-',
    },
    projectData: {
      pages: [
        {
          name: "",
          component: `<!DOCTYPE html>
  <html lang="en">
    <head>
      <meta http-equiv="Content-Type" content="text/html; charset=UTF-8" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0"/>
      <meta http-equiv="X-UA-Compatible" content="IE=edge"/>
      <title>Email</title>
      <style>
        body {
          margin: 0 !important;
          padding: 0 !important;
          width: 100% !important;
          -webkit-text-size-adjust: 100%;
          -ms-text-size-adjust: 100%;
          background: #fff;
        }
        table, td {
          mso-table-lspace: 0pt;
          mso-table-rspace: 0pt;
        }
        img {
          border: 0;
          height: auto;
          line-height: 100%;
          outline: none;
          text-decoration: none;
          display: block;
          max-width: 100%;
        }
        a {
          text-decoration: none;
          color: inherit;
        }
        * {
          box-sizing: border-box;
        }
      </style>
    </head>
    <body style="margin:0; padding:0; background:#fff;">
      <table border="0" cellpadding="0" cellspacing="0" width="100%">
        <tr>
          <td>
            <!-- Email content goes here -->
          </td>
        </tr>
      </table>
    </body>
  </html>`
        }
      ],
    },
  };

export default function CustomEditor({ type, initialData }) {
  const dispatch = useDispatch();
  const { width } = useWindowDimensions();
  const [isVisible, setIsVisible] = useState(false);
  const [preview, setPreview] = useState('');
  const webViewRef = useRef<WebView>(null);
  const editor = useEditorMaybe();

  // This useEffect handles all editor configuration once the instance is ready.
  useEffect(() => {
    if (!editor) return;

    // --- Asset Manager Configuration ---
    const am = editor.AssetManager;

    // Define the custom upload function
    am.getConfig().uploadFile = async (ev) => {
      const file = ev.dataTransfer ? ev.dataTransfer.files[0] : ev.target.files[0];
      if (!file) return;

      // Trigger the upload start event
      editor.trigger('asset:upload:start');

      const reader = new FileReader();
      reader.readAsDataURL(file);

      reader.onload = async () => {
        try {
          // 1. Upload the asset using your API
          const response = await apis.uploadGlobalAsset({ name: file.name, data: reader.result });

          // 2. Ensure the response is an array of asset objects or URLs
          const newAssets = Array.isArray(response) ? response : [response];

          // 3. THIS IS THE FIX: Manually add the new assets to the manager
          setTimeout(()=> am.add(newAssets), 200);

          // 4. Trigger the upload end event
          editor.trigger('asset:upload:end');

        } catch (error) {
          console.error('Error during file upload:', error);
          dispatch({ type: SHOW_AUTH_MODAL, data: { content: `Error uploading file: ${error}`, type: "error" } });
          editor.trigger('asset:upload:error', error);
        }
      };

      reader.onerror = (error) => {
        editor.trigger('asset:upload:error', error);
      };
    };

  }, [editor, dispatch]);


  const _duplicateCallback = async (title: string) => {
    if (!editor) return;
    const newTitle = window.prompt('Enter the new title for the duplicated template', title);
    if (newTitle) {
      duplicate(title, editor, newTitle, setPreview, setIsVisible);
    }
  };

  const _saveCallback = async (title: string) => {
    if (!editor) return;
    const getTemplate = () => {
      const html = editor.getHtml();
      const css = editor.getCss();
      const head = editor.Canvas.getDocument().head.innerHTML;
      return `<!DOCTYPE html><html><head>${head}<style>${css}</style></head>${html.includes('<body>') ? html : `<body>${html}</body>`}</html>`;
    };

    // Extract dynamic text components
    const extractDynamicText = () => {
      const dynamicTextArray: string[] = [];
      const components = editor.getComponents();

      const traverseComponents = (comps: any) => {
        comps.each((comp: any) => {
          if (comp.get('type') === 'dynamic-text') {
            const content = comp.get('content') || comp.components().map((c: any) => c.get('content')).join('') || '{{Dynamic Text}}';
            const placeholder = typeof content === 'string' ? content : '{{Dynamic Text}}';
            // Only add unique placeholders
            if (placeholder && !dynamicTextArray.includes(placeholder)) {
              dynamicTextArray.push(placeholder);
            }
          }
          // Recursively check nested components
          if (comp.components && comp.components().length > 0) {
            traverseComponents(comp.components());
          }
        });
      };

      traverseComponents(components);
      return dynamicTextArray;
    };

    try {
      const canvas = editor.Canvas.getBody();
      const canvasImage = await html2canvas(canvas, { useCORS: true });
      const snapShot = canvasImage.toDataURL("image/png");
      const dynamicText = extractDynamicText();

      const payload = {
        title: title,
        content: getTemplate(),
        snapShot: snapShot,
        dynamicText: dynamicText,
        _id: initialData?._id
      };
      await apis.updateEmailTemplate(payload);
      Alert.alert("Success", "Template saved successfully.");
    } catch (error) {
      console.error('Error in save callback:', error);
    }
  };

  return (
    <View>
      <Modal isVisible={isVisible} style={{ zIndex: 9999 }} extraChildren={(<Button customStyle={{ top: 100, minWidth: 120 }} buttonText={"Close"} defaultColor='red' onPress={() => setIsVisible(false)} />)}>
        <WebView 
          style={{ minHeight: "90%", borderRadius: 20, minWidth: "80%" }} 
          ref={webViewRef} 
          source={{ html: preview }}
          injectedJavaScript={`
            const viewport = document.querySelector('meta[name="viewport"]');
            if (viewport) {
              viewport.setAttribute('content', 'width=${width * 0.8}, initial-scale=1.0, maximum-scale=1.0');
            } else {
              const meta = document.createElement('meta');
              meta.name = 'viewport';
              meta.content = 'width=${width * 0.8}, initial-scale=1.0, maximum-scale=1.0';
              document.getElementsByTagName('head')[0].appendChild(meta);
            }
            true;
          `}
        />
      </Modal>
      <View style={{ flex: 1 }}>
        <Topbar
          type={type}
          saveCallback={() => _saveCallback(initialData?.title || 'Untitled')}
          duplicateCallback={() => _duplicateCallback(initialData?.title || 'Untitled')}
          projectData={{ _id: initialData?._id, title: initialData?.title }}
        />
        <View style={{ flex: 1, flexDirection: "row" }}>
          <View style={{ marginTop: 10, flex: 1, marginLeft: -130, paddingLeft: 90 }}>
            <RightSidebar type={type} disablePages />
          </View>
          <View style={{ flex: 3, flexGrow: 3, borderColor: "rgb(90,192,234)", padding: 10, borderLeftWidth: 1, borderRadius: 5 }}>
            <Canvas />
          </View>
        </View>
      </View>
    </View>
  );
}
